import { CONFIDENCE_THRESHOLD, type LoadListRow } from "./api";
import { formatAmount, formatLoadDate, formatMoveType } from "./format";

// Pure display logic for the home list, kept out of the component so it
// can be unit-tested headlessly (the pattern maintenance-web and
// interchange-web use for their derived rows).

export interface LoadListDisplayRow {
  id: number;
  href: string;
  // Container number is the driver's primary handle on a load; "—" when
  // the parser couldn't read one (they can fix it on the detail screen).
  containerLabel: string;
  // Secondary line: move type · date, skipping parts we don't have so a
  // row never renders a dangling separator.
  metaLabel: string;
  // Formatted money, or null when the load hasn't been priced yet — the
  // list renders an "Add amount" affordance in that case. This is the
  // whole point of the module, so it is a first-class field, not a
  // formatted string with a placeholder baked in.
  amountLabel: string | null;
  needsAmount: boolean;
  // Parser wasn't confident about this ticket; worth a look.
  lowConfidence: boolean;
}

export function buildLoadListRows(loads: LoadListRow[]): LoadListDisplayRow[] {
  return loads.map((row) => {
    const amountLabel = formatAmount(row.amount);
    const meta = [formatMoveType(row.moveType), formatLoadDate(row.loadDate)].filter(
      (part): part is string => part !== null && part !== "—",
    );

    return {
      id: row.id,
      href: `/l/${row.id}`,
      containerLabel: row.containerNo ?? "—",
      metaLabel: meta.join(" · "),
      amountLabel,
      needsAmount: amountLabel === null,
      lowConfidence:
        row.parserConfidence != null && row.parserConfidence < CONFIDENCE_THRESHOLD,
    };
  });
}

// How many of these loads still need a price. Drives the header count —
// the driver's actual to-do.
export function countNeedingAmount(rows: LoadListDisplayRow[]): number {
  return rows.filter((r) => r.needsAmount).length;
}

// Sum of the priced loads, as a number. Unpriced loads contribute 0 —
// they are counted separately by countNeedingAmount so the driver can
// see the total is incomplete rather than silently wrong.
export function sumPricedAmount(loads: LoadListRow[]): number {
  return loads.reduce((acc, row) => {
    const n = row.amount === null ? NaN : Number(row.amount);
    return Number.isFinite(n) ? acc + n : acc;
  }, 0);
}
